import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { tokenStorage } from "@src/lib/settings/tokenStorage";
import { defaultStorage } from "@src/lib/settings/defaultStorage";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import type { WalterUser } from "@src/lib/auth/OAuthService";
import { useOfficeInitialized } from "./useOfficeInitialized";

/**
 * Hook that handles authentication state checking and updates external state.
 *
 * This hook checks for authentication tokens and updates the provided setter function
 * to maintain a single source of truth for authentication state.
 */
export const useAuthState = (setIsLoggedIn: (isLoggedIn: boolean) => void) => {
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const officeReady = useOfficeInitialized();

  useEffect(() => {
    if (!officeReady || isAuthChecked) return;

    async function checkAuthState() {
      try {
        const token = await tokenStorage.get("walter");

        if (token) {
          setIsLoggedIn(true);

          // Set up Sentry user context
          const currentUser = defaultStorage.get<WalterUser>("currentUser");
          const scope = Sentry.getCurrentScope();
          scope.clear();
          scope.setUser({
            id: currentUser?.id,
            email: currentUser?.email,
          });
        } else {
          setIsLoggedIn(false);
        }

        setIsAuthChecked(true);
      } catch (error) {
        captureErrorInfo(error);
        setIsLoggedIn(false);
        setIsAuthChecked(true);
      }
    }

    void checkAuthState();
  }, [officeReady, isAuthChecked, setIsLoggedIn]);

  return isAuthChecked;
};
